/** @jsxImportSource @emotion/react */
import * as Yup from 'yup';
import { css } from '@emotion/react';
import { createRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { Form, FormikProvider, useFormik } from 'formik';

import {
  BLACK,
  WHITE,
  GREEN_LIGHT,
  PURPLE_FEEK,
  PURPLE_LIGHT,
  PURPLE_ELECTRIC,
  PINK_LIGHT_SECUNDARY,
} from 'styles/colors';
import APIClient from 'APIClient';
import Text from 'components/Text';
import Button from 'components/buttons/Button';
import handleError from 'utils/handleError';
import ButtonPrimary from 'components/buttons/ButtonPrimary';
import { MEDIA_QUERIES } from 'styles/constants';
import { FormInputText } from 'components/inputs/InputText';
import { FormPhoneInput } from 'components/inputs/PhoneInput';
import { FormInputSelect } from 'components/inputs/InputSelect';
import { convertGooglePlaceToAddress } from 'utils/format';
import { FormPlacesAutoCompleteInput } from 'components/inputs/PlacesAutocompleteInput';
import { ReactComponent as FeekerBehindPhone } from 'assets/images/FeekerBehindPhone.svg';

const reacaptchaSiteKey = process.env.REACT_APP_RECAPTCHA_V3_SITE_KEY;

const validationSchema = Yup.object({
  name: Yup.string().required('Necesitamos el nombre de tu negocio'),
  contact: Yup.string().required('Necesitamos tu nombre completo'),
  feekspot_type: Yup.string()
    .oneOf([
      'Restaurant',
      'Coffee',
      'Bar',
      'Snacks',
      'Shopping',
      'Fitness',
      'Beauty',
      'Hobby',
    ])
    .required('Debes elegir el giro de tu negocio'),
  phone: Yup.string()
    .required('El télefono es requerido')
    .min(12, 'El télefono debe tener más de 10 numeros'),
  email: Yup.string()
    .email('Introduce una dirección de email válida')
    .required('Necesitamos tu email'),
  location: Yup.object().shape({
    formatedLocation: Yup.string().required(
      'Necesitamos la dirección aproximada de tu negocio',
    ),
  }),
});

const initialValues = {
  name: '',
  contact: '',
  feekspot_type: '',
  phone: '',
  email: '',
  location: '',
};

const feekspotTypes = [
  { value: 'Restaurant', label: 'Restaurant' },
  { value: 'Coffee', label: 'Coffee' },
  { value: 'Bar', label: 'Bar' },
  { value: 'Snacks', label: 'Snacks' },
  { value: 'Shopping', label: 'Shopping' },
  { value: 'Fitness', label: 'Fitness' },
  { value: 'Beauty', label: 'Beauty' },
  { value: 'Hobby', label: 'Hobby' },
];

const styles = css`
  display: flex;
  margin-top: 30px;
  flex-direction: column;
  align-items: center;
  width: 450px;
  margin-inline-end: 7%;

  .signInForm {
    width: 100%;
    height: fit-content;
    display: flex;
    flex-direction: column;
    gap: 16px;
    .error-text {
      display: none;
    }
    margin-top: 1em;
    background-color: ${PURPLE_ELECTRIC};
    border-radius: 30px;
    border: 1px solid ${PINK_LIGHT_SECUNDARY};
    padding: 24px;

    .signInFormInput::placeholder,
    .signInFormInput input::placeholder,
    .signInFormInput select::placeholder {
      color: rgba(255, 255, 255, 0.5);
    }

    .signInFormInput,
    .signInFormInput input,
    .signInFormInput select {
      color: ${WHITE};
    }

    .signInFormInput input:not(.phoneInput) {
      padding-block: 0;
      display: block;
    }

    .signInFormInput select {
      padding-block: 12px;
    }

    .signInFormInput,
    .locationInput input,
    .feekspotTypeWrapper .inputWrapper {
      height: 48px;
      border-radius: 10px;
      border: 1px solid ${PURPLE_LIGHT};
      background-color: rgba(239, 238, 255, 0.2);
      min-width: unset;
      select:required:invalid {
        color: red;
      }
      .selected-flag {
        border-top-left-radius: 10px;
        border-bottom-left-radius: 10px;
      }
      .flag-dropdown.btn {
        border-color: ${PURPLE_LIGHT};
      }
      &,
      .form-control .phoneInput {
        font-size: 16px;
      }
    }

    .submitSignInFormButton {
      width: 196px;
      height: 60px;
      border-radius: 40px;
      margin-left: auto;
    }
  }

  .successSignInMsg {
    display: flex;
    gap: 20px;
    position: absolute;
    bottom: 0;
    margin: 24px;
    background-color: ${GREEN_LIGHT};
    padding: 40px;
    border-radius: 20px;

    .successSignInMsgTextWrapper {
      flex: 1;
      display: flex;
      flex-direction: column;
      align-content: center;
      justify-content: center;
      gap: 20px;

      .successSignInMsgButton {
        display: flex;
        justify-content: center;
        align-items: center;
        height: 48px;
        padding-inline: 25px;
        border-radius: 24px;
        place-self: start center;
        font-size: 20px;
        font-weight: 600;
        color: ${WHITE};
        background-color: ${BLACK};
      }
    }
  }

  ${MEDIA_QUERIES.mobile} {
    margin: 0;
    width: 100%;

    .successSignInMsg {
      margin: 20px;
      padding: 20px;
      border-radius: 10px;

      .successSignInMsgTextWrapper {
        > * {
          text-align: center;
        }
        .successSignInMsgButton {
          place-self: center;
          font-size: 16px;
        }
      }
    }
  }
`;

function SignInForm() {
  const [showSuccess, setShowSuccess] = useState(false);
  const recaptchaRef = createRef();

  const formik = useFormik({
    validateOnMount: true,
    onSubmit: handleSubmit,
    enableReinitialize: true,
    initialValues,
    validationSchema,
  });

  async function handleSubmit(values, actions) {
    actions.setSubmitting(true);
    const token = await recaptchaRef.current.executeAsync();
    const {
      administrative_area_level_1: state,
      street_number: exterior_number,
      locality: city,
      postal_code: cp,
      sublocality_level_1: suburb,
      route: street,
      country,
    } = convertGooglePlaceToAddress(
      values.location.addressComponents,
    );

    APIClient.feekspotSignInRequest({
      ...values,
      location: {
        state,
        exterior_number,
        city,
        cp,
        suburb,
        street,
        country,
        coordinates: [values.location.lat, values.location.lng],
      },
      token,
    })
      .then(() => {
        setShowSuccess(true);
        actions.resetForm();
      })
      .catch(handleError)
      .finally(() => actions.setSubmitting(false));
  }

  return (
    <article css={styles}>
      <FormikProvider value={formik}>
        <Form className="signInForm">
          <FormInputText
            inputClassName="signInFormInput"
            name="contact"
            placeholder="Nombre completo"
          />
          <FormInputText
            inputClassName="signInFormInput"
            name="email"
            placeholder="Email"
          />
          <FormPhoneInput
            inputClassName="signInFormInput"
            name="phone"
            backgroundColor="rgba(239, 238, 255, 0.2)"
            textColor={WHITE}
          />
          <FormPlacesAutoCompleteInput
            placeholder="Dirección del negocio"
            inputClassName="signInFormInput"
            name="location"
          />
          <FormInputText
            inputClassName="signInFormInput"
            name="name"
            placeholder="Nombre de tu negocio"
          />
          <FormInputSelect
            name="feekspot_type"
            placeholder="Seleccionar giro"
            options={feekspotTypes}
            className="feekspotTypeWrapper"
            inputClassName="signInFormInput"
          />
          <div className="d-flex justify-content-center">
            <ReCAPTCHA
              size="invisible"
              ref={recaptchaRef}
              sitekey={reacaptchaSiteKey}
            />
          </div>
          <ButtonPrimary
            onClick={formik.submitForm}
            label="Enviar solicitud"
            fontSize={20}
            textColor={PURPLE_FEEK}
            color={GREEN_LIGHT}
            textProps={{ fontWeight: 500 }}
            className="submitSignInFormButton"
          />
        </Form>
      </FormikProvider>

      {showSuccess && (
        <aside className="successSignInMsg">
          <div className="successSignInMsgTextWrapper">
            <Text color={BLACK} fontSize={24} fontWeight="600">
              ¡Mensaje enviado con éxito!
            </Text>
            <Text color={BLACK} fontSize={16}>
              En breve uno de nuestros ejecutivos se pondrá en
              contacto contigo
            </Text>
            <Button
              onClick={() => setShowSuccess(false)}
              className="successSignInMsgButton"
            >
              Entendido
            </Button>
          </div>
          <FeekerBehindPhone className="hideOnMobile" />
        </aside>
      )}
    </article>
  );
}

export default SignInForm;
